// src/components/auth/AuthSplitLayout.jsx
//
// Two-column black/cream split used by Sign-in and Password reset.
// Left ~52% black panel with brand statement; optional KPI ticker via leftStats.
// Right ~48% cream panel with the form.
import React from "react";
import { Box, useTheme } from "@mui/material";
import Logo from "../brand/Logo";
import Eyebrow from "../brand/Eyebrow";
import { fonts } from "../../theme/tokens";

export default function AuthSplitLayout({
  // Left panel
  leftEyebrow,
  leftTitle, // node — usually a serif headline with italic gold accent
  leftSubtitle,
  leftStats, // optional: [{ n, l }] — small "stat ticker" row
  leftFooter, // optional: small footer text in left panel
  // Right panel
  children,
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        backgroundColor: theme.palette.brand.bg,
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* LEFT — black brand panel */}
      <Box
        sx={{
          flex: { xs: "0 0 0%", md: "1.05" },
          display: { xs: "none", md: "flex" },
          backgroundColor: theme.palette.brand.black,
          color: "#fff",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          p: "36px 48px",
        }}
      >
        {/* Cyan stripe */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: theme.palette.brand.cyan,
          }}
        />
        {/* Soft gold radial glow */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 90% 0%, rgba(201,161,75,0.08), transparent 50%)",
          }}
        />

        {/* Top — logo */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Logo dark lg />
        </Box>

        {/* Middle — headline */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          {leftEyebrow && (
            <Eyebrow sx={{ color: theme.palette.brand.cyan, mb: 2.5 }}>
              {leftEyebrow}
            </Eyebrow>
          )}
          {leftTitle && (
            <Box
              component="h1"
              sx={{
                fontFamily: fonts.serif,
                fontSize: 56,
                fontWeight: 500,
                lineHeight: 1.0,
                letterSpacing: "-0.025em",
                m: 0,
                mb: "22px",
              }}
            >
              {leftTitle}
            </Box>
          )}
          {leftSubtitle && (
            <Box
              sx={{
                fontSize: 15,
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.6,
                m: 0,
                maxWidth: 380,
              }}
            >
              {leftSubtitle}
            </Box>
          )}
          {leftStats && leftStats.length > 0 && (
            <Box sx={{ mt: 5.5, display: "flex", gap: 4.5 }}>
              {leftStats.map((s, i) => (
                <Box key={i}>
                  <Box
                    sx={{
                      fontFamily: fonts.serif,
                      fontSize: 30,
                      fontWeight: 600,
                      color: "#fff",
                      lineHeight: 1,
                    }}
                  >
                    {s.n}
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11,
                      color: "rgba(255,255,255,0.5)",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      mt: 0.75,
                    }}
                  >
                    {s.l}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Bottom — small footer */}
        {leftFooter && (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              fontSize: 11.5,
              color: "rgba(255,255,255,0.4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {leftFooter}
          </Box>
        )}
      </Box>

      {/* RIGHT — form panel */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, md: 5 },
          backgroundColor: theme.palette.brand.bg,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 380 }}>{children}</Box>
      </Box>
    </Box>
  );
}
